import React from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  AppBar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  Drawer,
  Grid,
  IconButton,
  Menu,
  MenuItem,
  Paper,
  Stack,
  Toolbar,
  Typography,
} from "@mui/material";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import ThunderstormIcon from "@mui/icons-material/Thunderstorm";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import LockIcon from "@mui/icons-material/Lock";
import BoltIcon from "@mui/icons-material/Bolt";
import SearchIcon from "@mui/icons-material/Search";
import DescriptionIcon from "@mui/icons-material/Description";
import BadgeIcon from "@mui/icons-material/Badge";
import HomeIcon from "@mui/icons-material/Home";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import ElderlyOutlinedIcon from "@mui/icons-material/ElderlyOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import ShieldIcon from "@mui/icons-material/Shield";
import GroupsIcon from "@mui/icons-material/Groups";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import TuneIcon from "@mui/icons-material/Tune";
import Inventory2Icon from "@mui/icons-material/Inventory2";
import BlockIcon from "@mui/icons-material/Block";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LanguageIcon from "@mui/icons-material/Language";
import AddIcon from "@mui/icons-material/Add";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import { useTranslation } from "react-i18next";
import LanguageSelector from "../common/LanguageSelector";
import "../page2026ver2/Hero.css";
import appScreenshotLight from "../../Assets/app-ss-light.png";
import appScreenshotDark from "../../Assets/app-ss-dark.png";
import logo from "../../Assets/logo.png";
import logoDark from "../../Assets/logo-dark.png";
import "../../styles/Page2026.css";
import urlBase from "../url/urlBase";
import { appStore, googlePlay } from "../url/downloadUrl";
import PrivacyPolicy from "../privacyPolicy/PrivacyPolicy";
import Terms from "../termAndConditions/terms";
import Security2026 from "./sections/Security2026";
import Pricing2026 from "./sections/Pricing2026";
import { useGeoPricing } from "../../geo/GeoPricingContext";
import PriceChunks from "../common/PriceChunks";
import ThemeToggle from "../ui/ThemeToggle";
import { useTheme } from "../../theme";

const colors = {
  teal: "#00DCBC",
  tealDark: "#02B096",
  blue: "#18B4EC",
  ink: "var(--bv-ink)",
  ink60: "var(--bv-ink-60)",
  ink40: "var(--bv-ink-40)",
  bg: "var(--bv-bg)",
  border: "var(--bv-border)",
};

const headingFont = '"Sora", "Poppins", sans-serif';
const bodyFont = '"DM Sans", "Inter", "Poppins", sans-serif';

const REVEAL_DURATION_MS = 680;
const REVEAL_STAGGER_MS = 72;
const REVEAL_ITEM_BASE_DELAY_MS = 140;
const REVEAL_NAV_OFFSET_PX = 96;
const REVEAL_MIN_VISIBLE_RATIO = 0.14;

const primaryBtnSx = {
  backgroundColor: colors.teal,
  color: "#fff",
  px: { xs: 3, md: 5 },
  py: { xs: 1.25, md: 1.6 },
  borderRadius: "50px",
  textTransform: "none",
  fontWeight: 600,
  fontFamily: "Helvetica Neue",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: colors.tealDark,
    boxShadow: "0px 2px 8px rgba(0, 220, 188, 0.3)",
  },
};

const secondaryBtnSx = {
  backgroundColor: "#1a2b4a",
  borderColor: "#1a2b4a",
  borderWidth: "1px",
  color: "#fff",
  px: { xs: 3, md: 5 },
  py: { xs: 1.25, md: 1.6 },
  borderRadius: "50px",
  textTransform: "none",
  fontWeight: 600,
  fontFamily: "Helvetica Neue",
  "&:hover": {
    borderColor: "#16253f",
    backgroundColor: "#16253f",
    borderWidth: "1px",
  },
};

const headerBtnSx = {
  px: { xs: 1.4, xl: 1.8 },
  py: { xs: 0.75, xl: 0.9 },
  minWidth: "auto",
  fontSize: { xs: "0.68rem", xl: "0.72rem" },
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const navLinkSx = {
  color: colors.ink60,
  textDecoration: "none",
  fontWeight: 600,
  fontSize: { xl: "0.8rem" },
  whiteSpace: "nowrap",
  transition: "color 180ms ease",
  "&:hover": { color: colors.tealDark },
};

const cardHoverSx = {
  transition: "transform 200ms ease, box-shadow 200ms ease, border-color 200ms ease",
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: `${colors.teal} !important`,
      boxShadow: "0 14px 28px rgba(15, 23, 42, 0.10)",
    },
  },
};

const screenshotBlockHoverSx = {
  transition: "transform 220ms ease, box-shadow 220ms ease, border-color 220ms ease",
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": {
      transform: "translateY(-6px)",
      borderColor: `${colors.teal} !important`,
      boxShadow: "0 16px 30px rgba(15, 23, 42, 0.12)",
    },
  },
};

const mobileCardSpacingSx = {
  "@media (max-width: 600px)": {
    marginBottom: 1.2,
  },
};

const whoFlexSx = {
  display: "flex",
  flexWrap: "wrap",
  gap: { xs: "12px", sm: "10px", md: "8px", lg: "6px" },
  width: "100%",
  alignItems: "stretch",
  boxSizing: "border-box",
};

const whoCardSx = {
  p: { xs: 2, sm: 2.25, md: 2.25 },
  boxSizing: "border-box",
  flexGrow: 1,
  flexShrink: 0,
  flexBasis: {
    xs: "100%",
    sm: "calc((100% - 10px) / 2)",
    md: "calc((100% - 16px) / 3)",
    lg: "calc((100% - 24px) / 5)",
  },
  maxWidth: {
    xs: "100%",
    sm: "calc((100% - 10px) / 2)",
    md: "calc((100% - 16px) / 3)",
    lg: "calc((100% - 24px) / 5)",
  },
  minWidth: 0,
  minHeight: 0,
  bgcolor: "var(--bv-card-bg)",
  border: "1.5px solid var(--bv-border) !important",
  borderRadius: "16px !important",
  display: "flex",
  flexDirection: "column",
  justifyContent: "flex-start",
  gap: { xs: 1, md: 1.25 },
  textAlign: "left",
  overflow: "hidden",
  boxShadow: "0 4px 20px rgba(15, 23, 42, 0.06) !important",
  transition: "transform 0.3s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.3s cubic-bezier(0.16, 1, 0.3, 1), border-color 0.3s ease",
  "@media (hover: hover) and (pointer: fine)": {
    "&:hover": {
      transform: "translateY(-5px)",
      borderColor: `${colors.teal} !important`,
      boxShadow: "0 22px 48px rgba(15, 23, 42, 0.12) !important",
      "& .who-icon-wrap": { bgcolor: "rgba(0, 220, 188, 0.12)" },
      "& .who-link": { gap: "8px" },
    },
  },
};

export default function Page2026Scratch() {
  const { theme } = useTheme();
  const { t, i18n } = useTranslation(["common", "home", "legal"]);
  const { formatPrice, convertPriceString } = useGeoPricing();

  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const title = t("common:meta.title");
    const description = t("common:meta.description");

    if (title) {
      document.title = title;
    }

    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc && description) {
      metaDesc.setAttribute("content", description);
    }

    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle && title) {
      ogTitle.setAttribute("content", title);
    }

    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc && description) {
      ogDesc.setAttribute("content", description);
    }

    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle && title) {
      twTitle.setAttribute("content", title);
    }

    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc && description) {
      twDesc.setAttribute("content", description);
    }

    document.documentElement.lang = i18n.language || "en";
  }, [t, i18n.language]);
  React.useEffect(() => {
    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = "https://fonts.googleapis.com";
    preconnect.setAttribute("data-page2026-fonts", "true");

    const preconnectStatic = document.createElement("link");
    preconnectStatic.rel = "preconnect";
    preconnectStatic.href = "https://fonts.gstatic.com";
    preconnectStatic.crossOrigin = "anonymous";
    preconnectStatic.setAttribute("data-page2026-fonts", "true");

    const stylesheet = document.createElement("link");
    stylesheet.rel = "stylesheet";
    stylesheet.href =
      "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&family=Sora:wght@600;700;800&display=swap";
    stylesheet.setAttribute("data-page2026-fonts", "true");

    document.head.appendChild(preconnect);
    document.head.appendChild(preconnectStatic);
    document.head.appendChild(stylesheet);

    return () => {
      document
        .querySelectorAll('link[data-page2026-fonts="true"]')
        .forEach((node) => node.remove());
    };
  }, []);

  const [openFaq, setOpenFaq] = React.useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [downloadMenuAnchor, setDownloadMenuAnchor] = React.useState(null);
  const [mobileDownloadMenuAnchor, setMobileDownloadMenuAnchor] = React.useState(null);
  const [legalModalType, setLegalModalType] = React.useState(null);

  const legalTypeFromHash = React.useCallback((hash) => {
    const h = String(hash || "")
      .trim()
      .toLowerCase();
    if (h === "#privacy" || h === "#privacy-policy") return "privacy";
    if (h === "#terms" || h === "#terms-and-conditions") return "terms";
    return null;
  }, []);

  const openLegalModal = React.useCallback((type) => {
    if (type !== "privacy" && type !== "terms") return;
    setLegalModalType(type);
    const nextHash = type === "privacy" ? "#privacy" : "#terms";
    if (window.location.hash.toLowerCase() !== nextHash) {
      window.history.pushState(null, "", nextHash);
    }
  }, []);

  const closeLegalModal = React.useCallback(() => {
    setLegalModalType(null);
    if (legalTypeFromHash(window.location.hash)) {
      window.history.pushState(
        null,
        "",
        `${window.location.pathname}${window.location.search}`
      );
    }
  }, [legalTypeFromHash]);

  React.useEffect(() => {
    const syncFromHash = () => {
      setLegalModalType(legalTypeFromHash(window.location.hash));
    };
    syncFromHash();
    window.addEventListener("hashchange", syncFromHash);
    window.addEventListener("popstate", syncFromHash);
    return () => {
      window.removeEventListener("hashchange", syncFromHash);
      window.removeEventListener("popstate", syncFromHash);
    };
  }, [legalTypeFromHash]);

  const navLinks = [
    { label: t("common:nav.howItWorks"), href: "#how-it-works" },
    { label: t("common:nav.security"), href: "#security" },
    { label: t("common:nav.pricing"), href: "#pricing" },
    { label: t("common:nav.faqs"), href: "#faqs" },
  ];

  const ver2Benefits = [
    { icon: <ShieldIcon fontSize="small" />, strong: t("home:hero.benefits.peaceOfMindStrong"), text: t("home:hero.benefits.peaceOfMindText") },
    { icon: <NotificationsActiveIcon fontSize="small" />, strong: t("home:hero.benefits.proactiveProtectionStrong"), text: t("home:hero.benefits.proactiveProtectionText") },
    { icon: <FavoriteIcon fontSize="small" />, strong: t("home:hero.benefits.crisisReadinessStrong"), text: t("home:hero.benefits.crisisReadinessText") },
  ];

  const ver2Categories = [
    {
      icon: <FlightTakeoffIcon fontSize="small" sx={{ color: "#1AADEE" }} />,
      title: t("home:hero.categories.travel.title"),
      scenario: t("home:hero.categories.travel.scenario"),
      outcome: t("home:hero.categories.travel.outcome"),
    },
    {
      icon: <LocalHospitalIcon fontSize="small" sx={{ color: "#1AADEE" }} />,
      title: t("home:hero.categories.medical.title"),
      scenario: t("home:hero.categories.medical.scenario"),
      outcome: t("home:hero.categories.medical.outcome"),
    },
    {
      icon: <ThunderstormIcon fontSize="small" sx={{ color: "#1AADEE" }} />,
      title: t("home:hero.categories.disaster.title"),
      scenario: t("home:hero.categories.disaster.scenario"),
      outcome: t("home:hero.categories.disaster.outcome"),
    },
    {
      icon: <NotificationsActiveIcon fontSize="small" sx={{ color: "#1AADEE" }} />,
      title: t("home:hero.categories.reminders.title"),
      scenario: t("home:hero.categories.reminders.scenario"),
      outcome: t("home:hero.categories.reminders.outcome"),
    },
  ];

  const testimonials = [
    { name: t("home:testimonials.sarah.name"), role: t("home:testimonials.sarah.role"), text: t("home:testimonials.sarah.text") },
    { name: t("home:testimonials.robert.name"), role: t("home:testimonials.robert.role"), text: t("home:testimonials.robert.text") },
    { name: t("home:testimonials.linda.name"), role: t("home:testimonials.linda.role"), text: t("home:testimonials.linda.text") },
  ];

  const rawWithout = t("home:reality.without", { returnObjects: true });
  const rawWith = t("home:reality.with", { returnObjects: true });
  const withoutList = Array.isArray(rawWithout) ? rawWithout : [];
  const withList = Array.isArray(rawWith) ? rawWith : [];

  const whoItsFor = [
    {
      Icon: PersonOutlineIcon,
      title: t("home:whoItsFor.individuals.title"),
      text: t("home:whoItsFor.individuals.text"),
    },
    {
      Icon: FavoriteBorderIcon,
      title: t("home:whoItsFor.couples.title"),
      text: t("home:whoItsFor.couples.text"),
    },
    {
      Icon: GroupsOutlinedIcon,
      title: t("home:whoItsFor.families.title"),
      text: t("home:whoItsFor.families.text"),
    },
    {
      Icon: ElderlyOutlinedIcon,
      title: t("home:whoItsFor.agingParents.title"),
      text: t("home:whoItsFor.agingParents.text"),
    },
    {
      Icon: ShieldOutlinedIcon,
      title: t("home:whoItsFor.seniors.title"),
      text: t("home:whoItsFor.seniors.text"),
    },
  ];

  const securitySteps = [
    {
      title: t("home:security.step1Title"),
      body: t("home:security.step1Body"),
    },
    {
      title: t("home:security.step2Title"),
      body: t("home:security.step2Body"),
    },
    {
      title: t("home:security.step3Title"),
      body: t("home:security.step3Body"),
    },
    {
      title: t("home:security.step4Title"),
      body: t("home:security.step4Body"),
    },
  ];

  const securityFeatures = [
    {
      icon: <TuneIcon fontSize="small" />,
      title: t("home:security.features.f1Title"),
      text: t("home:security.features.f1Text"),
    },
    {
      icon: <Inventory2Icon fontSize="small" />,
      title: t("home:security.features.f2Title"),
      text: t("home:security.features.f2Text"),
    },
    {
      icon: <BlockIcon fontSize="small" />,
      title: t("home:security.features.f3Title"),
      text: t("home:security.features.f3Text"),
    },
    {
      icon: <ShieldIcon fontSize="small" />,
      title: t("home:security.features.f4Title"),
      text: t("home:security.features.f4Text"),
    },
  ];



  const rawFaqItems = t("home:faq.items", { returnObjects: true });
  const faqItems = Array.isArray(rawFaqItems) ? rawFaqItems : [];

  React.useEffect(() => {
    const sections = Array.from(document.querySelectorAll("[data-reveal]"));
    if (!sections.length) return undefined;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    const markRevealed = (node) => {
      node.classList.add("reveal-visible", "reveal-done");
    };

    const assignItemStagger = () => {
      sections.forEach((section) => {
        const items = section.querySelectorAll("[data-reveal-item]");
        items.forEach((item, index) => {
          item.style.setProperty("--reveal-item-index", String(index));
        });
      });
    };

    const isNearPageEnd = () =>
      window.scrollY + window.innerHeight >= document.documentElement.scrollHeight - 64;

    const getVisibleHeight = (rect, viewHeight) =>
    assignItemStagger();

    const isMobileScreen = window.innerWidth < 900;
    if (prefersReducedMotion || isMobileScreen) {
      sections.forEach(markRevealed);
      return undefined;
    }

    const shouldRevealNow = (entry) => {
      if (!entry.isIntersecting) return false;
      const rect = entry.boundingClientRect;
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      return rect.top <= viewHeight * 0.92 && rect.bottom > 0;
    };

    const revealStuckSections = () => {
      const viewHeight = window.innerHeight || document.documentElement.clientHeight;
      sections.forEach((section) => {
        if (section.classList.contains("reveal-done")) return;
        const rect = section.getBoundingClientRect();
        if (rect.top <= viewHeight * 0.95 && rect.bottom > 0) {
          markRevealed(section);
          observer.unobserve(section);
        }
      });
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!shouldRevealNow(entry)) return;
          markRevealed(entry.target);
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: [0, 0.05, 0.1],
        rootMargin: "0px 0px -5% 0px",
      }
    );

    sections.forEach((section) => {
      if (section.hasAttribute("data-reveal-immediate")) {
        markRevealed(section);
        return;
      }
      observer.observe(section);
    });

    window.addEventListener("scroll", revealStuckSections, { passive: true });
    window.addEventListener("resize", revealStuckSections, { passive: true });
    requestAnimationFrame(revealStuckSections);

    return () => {
      window.removeEventListener("scroll", revealStuckSections);
      window.removeEventListener("resize", revealStuckSections);
      observer.disconnect();
    };
  }, []);

  React.useEffect(() => {
    const onKeyDown = (event) => {
      const target = event.target;
      const editable =
        target instanceof HTMLElement &&
        (target.tagName === "INPUT" ||
          target.tagName === "TEXTAREA" ||
          target.tagName === "SELECT" ||
          target.isContentEditable);
      if (editable) return;

      const vh = window.innerHeight;
      const step = Math.round(vh * 0.88);
      const smallStep = Math.round(vh * 0.22);

      if (event.key === " " || event.code === "Space") {
        event.preventDefault();
        window.scrollBy({ top: event.shiftKey ? -step : step, behavior: "smooth" });
        return;
      }
      if (event.key === "PageDown") {
        event.preventDefault();
        window.scrollBy({ top: step, behavior: "smooth" });
        return;
      }
      if (event.key === "PageUp") {
        event.preventDefault();
        window.scrollBy({ top: -step, behavior: "smooth" });
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        window.scrollBy({ top: smallStep, behavior: "smooth" });
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        window.scrollBy({ top: -smallStep, behavior: "smooth" });
        return;
      }
      if (event.key === "Home") {
        event.preventDefault();
        window.scrollTo({ top: 0, behavior: "smooth" });
        return;
      }
      if (event.key === "End") {
        event.preventDefault();
        window.scrollTo({ top: document.documentElement.scrollHeight, behavior: "smooth" });
      }
    };

    window.addEventListener("keydown", onKeyDown, { passive: false });
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const handleDownloadMenuOpen = (event) => setDownloadMenuAnchor(event.currentTarget);
  const handleDownloadMenuClose = () => setDownloadMenuAnchor(null);
  const handleMobileDownloadMenuOpen = (event) => setMobileDownloadMenuAnchor(event.currentTarget);
  const handleMobileDownloadMenuClose = () => setMobileDownloadMenuAnchor(null);

  const downloadMenuPaperSx = {
    mt: 0.8,
    bgcolor: "var(--bv-card-bg)",
    border: `1px solid var(--bv-border)`,
    borderRadius: "14px",
    boxShadow: "0 12px 32px rgba(0, 0, 0, 0.28) !important",
    backgroundImage: "none",
    minWidth: 220,
    overflow: "hidden",
    p: 0.6,
  };

  const downloadMenuItemSx = {
    py: 1.2,
    px: 1.4,
    borderRadius: "8px",
    gap: 1.2,
    fontWeight: 600,
    fontFamily: "Helvetica Neue",
    fontSize: "0.76rem",
    letterSpacing: 0,
    textTransform: "none",
    color: "var(--bv-ink)",
    transition: "all 150ms ease",
    "&:not(:last-of-type)": {
      borderBottom: `1px solid var(--bv-border)`,
    },
    "&:hover": {
      backgroundColor: "var(--bv-surface-2, rgba(255, 255, 255, 0.08))",
    },
  };

  return (
    <Box
      sx={{
        fontFamily: bodyFont,
        color: colors.ink60,
        bgcolor: "var(--bv-surface)",
        scrollBehavior: "smooth",
        width: "100%",
        maxWidth: "100vw",
        boxSizing: "border-box",
        "& .MuiTypography-h1, & .MuiTypography-h2, & .MuiTypography-h3, & .MuiTypography-h4, & .MuiTypography-h5, & .MuiTypography-h6": {
          fontFamily: headingFont,
          letterSpacing: "-0.015em",
        },
        "& .MuiTypography-h2": {
          fontSize: "clamp(2rem, 5.4vw, 4.5rem)",
          color: colors.blue,
        },
        "& .MuiTypography-h3": {
          fontSize: "clamp(1.75rem, 4.2vw, 3.2rem)",
          color: colors.blue,
        },
        "& .MuiTypography-h6": {
          fontSize: "clamp(1rem, 1.6vw, 1.25rem)",
          color: colors.blue,
        },
        "& .MuiTypography-body1": {
          fontSize: "clamp(0.98rem, 1.15vw, 1.08rem)",
        },
        "& .MuiTypography-body2": {
          fontSize: "clamp(0.9rem, 1vw, 1rem)",
        },
        "@keyframes fragmentFadePulse": {
          "0%": { opacity: 0.45, transform: "scale(0.985)" },
          "50%": { opacity: 1, transform: "scale(1)" },
          "100%": { opacity: 0.45, transform: "scale(0.985)" },
        },
        "@keyframes heroBadgeFloat": {
          "0%": { transform: "translateY(0px)" },
          "50%": { transform: "translateY(-5px)" },
          "100%": { transform: "translateY(0px)" },
        },
        "@keyframes heroCardPulse": {
          "0%": { boxShadow: "0 0 0 rgba(0,220,188,0)" },
          "50%": { boxShadow: "0 0 0 3px rgba(0,220,188,0.18)" },
          "100%": { boxShadow: "0 0 0 rgba(0,220,188,0)" },
        },
        "& .MuiCard-root, & .MuiPaper-root:not(.who-card)": {
          boxShadow: "none",
          border: `1px solid ${colors.border}`,
        },
        "& .who-cards-flex": {
          isolation: "isolate",
        },
        "--reveal-y": "44px",
        "--reveal-duration": `${REVEAL_DURATION_MS}ms`,
        "@media (max-width: 899.95px)": {
          "--reveal-y": "0px",
          "& [data-reveal], & [data-reveal] [data-reveal-item]": {
            opacity: "1 !important",
            transform: "none !important",
            transition: "none !important",
          },
        },
        "& [data-reveal]": {
          opacity: 0,
          transform: "translate3d(0, var(--reveal-y), 0)",
          transition: `opacity var(--reveal-duration) cubic-bezier(0.22, 1, 0.36, 1), transform var(--reveal-duration) cubic-bezier(0.22, 1, 0.36, 1)`,
          willChange: "opacity, transform",
        },
        "& [data-reveal].reveal-visible, & [data-reveal].reveal-done": {
          opacity: 1,
          transform: "translate3d(0, 0, 0)",
        },
        "& [data-reveal] [data-reveal-item]": {
          opacity: 0,
          transform: "translate3d(0, calc(var(--reveal-y) * 0.65), 0)",
          transition: `opacity calc(var(--reveal-duration) * 0.85) cubic-bezier(0.22, 1, 0.36, 1), transform calc(var(--reveal-duration) * 0.85) cubic-bezier(0.22, 1, 0.36, 1)`,
          transitionDelay: `calc(${REVEAL_ITEM_BASE_DELAY_MS}ms + var(--reveal-item-index, 0) * ${REVEAL_STAGGER_MS}ms)`,
        },
        "& [data-reveal].reveal-visible [data-reveal-item], & [data-reveal].reveal-done [data-reveal-item]": {
          opacity: 1,
          transform: "translate3d(0, 0, 0)",
        },
        "@media (prefers-reduced-motion: reduce)": {
          "& [data-reveal], & [data-reveal] [data-reveal-item]": {
            opacity: 1,
            transform: "none",
            transition: "none",
          },
        },
      }}
    >
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: "var(--bv-appbar-bg)",
          width: "100%",
          maxWidth: "100vw",
          left: 0,
          right: 0,
          boxShadow: "0 1px 3px var(--bv-appbar-shadow)",
        }}
      >
        <Toolbar
          sx={{
            width: "100%",
            maxWidth: "100%",
            boxSizing: "border-box",
            px: { xs: 1.5, sm: 2.5, md: 3.5, lg: 4, xl: "52px" },
            minHeight: { xs: 66, sm: 72, md: 78 },
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: { xs: 1, xl: 1.5 },
          }}
        >
          <Box
            component="a"
            href="/"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              lineHeight: 0,
              flexShrink: 0,
              minWidth: 0,
            }}
          >
            <Box
              component="img"
              src={theme === "dark" ? logoDark : logo}
              alt={t("common:brand")}
              sx={{ width: { xs: 135, sm: 165, md: 195, lg: 215, xl: 240 }, height: "auto", maxWidth: "100%" }}
            />
          </Box>

          <Stack
            direction="row"
            spacing={{ lg: 1.2, xl: 1.5 }}
            sx={{
              display: { xs: "none", lg: "flex" },
              flex: 1,
              justifyContent: "center",
              alignItems: "center",
              minWidth: 0,
              mx: { lg: 1.5, xl: 2 },
              overflow: "hidden",
            }}
          >
            {navLinks.map((x, idx) => (
              <React.Fragment key={x.label}>
                <Typography component="a" href={x.href} variant="body2" sx={navLinkSx}>
                  {x.label}
                </Typography>
                {idx < navLinks.length - 1 && (
                  <Typography
                    aria-hidden
                    sx={{ color: colors.blue, fontWeight: 700, fontSize: 16, mx: 0.2, userSelect: "none" }}
                  >
                    |
                  </Typography>
                )}
              </React.Fragment>
            ))}
          </Stack>

          <Stack
            direction="row"
            spacing={{ lg: 0.6, xl: 0.75 }}
            alignItems="center"
            sx={{
              display: { xs: "none", lg: "flex" },
              flexShrink: 0,
            }}
          >
            <ThemeToggle />
            <LanguageSelector />
            <Button size="small" variant="outlined" onClick={handleDownloadMenuOpen} sx={{ ...secondaryBtnSx, ...headerBtnSx }}>
              {t("common:buttons.getApp")}
            </Button>
            <Button
              size="small"
              variant="contained"
              component="a"
              href={`${urlBase}signup`}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...primaryBtnSx, ...headerBtnSx }}
            >
              {t("common:buttons.trial30")}
            </Button>
            <Button
              size="small"
              variant="contained"
              component="a"
              href={urlBase}
              target="_blank"
              rel="noopener noreferrer"
              sx={{ ...primaryBtnSx, ...headerBtnSx, bgcolor: colors.blue, "&:hover": { bgcolor: "#0ea5e9" } }}
            >
              {t("common:buttons.login")}
            </Button>
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 0.8, sm: 1.2 }}
            alignItems="center"
            sx={{
              display: { xs: "inline-flex", lg: "none" },
              flexShrink: 0,
            }}
          >
            <ThemeToggle isMobile />
            <LanguageSelector isMobile />
            <IconButton
              onClick={() => setMobileMenuOpen(true)}
              sx={{
                color: colors.ink60,
                border: "1px solid var(--bv-border)",
                borderRadius: "12px",
                p: { xs: 0.7, sm: 0.9 },
                bgcolor: "var(--bv-surface)",
                "&:hover": {
                  bgcolor: "var(--bv-surface-2)",
                },
              }}
              aria-label={t("common:buttons.openMenu")}
            >
              <MenuIcon sx={{ fontSize: { xs: 20, sm: 22 } }} />
            </IconButton>
          </Stack>
        </Toolbar>
        <Menu
          anchorEl={downloadMenuAnchor}
          open={Boolean(downloadMenuAnchor)}
          onClose={handleDownloadMenuClose}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: downloadMenuPaperSx } }}
        >
          <MenuItem component="a" href={appStore} target="_blank" rel="noopener noreferrer" onClick={handleDownloadMenuClose} sx={downloadMenuItemSx}>
            <AppleIcon fontSize="small" sx={{ color: colors.blue }} />
            {t("common:buttons.iosDownload")}
          </MenuItem>
          <MenuItem component="a" href={googlePlay} target="_blank" rel="noopener noreferrer" onClick={handleDownloadMenuClose} sx={downloadMenuItemSx}>
            <AndroidIcon fontSize="small" sx={{ color: colors.tealDark }} />
            {t("common:buttons.androidDownload")}
          </MenuItem>
        </Menu>
      </AppBar>
      <Box sx={{ height: { xs: 66, sm: 72, md: 78 }, minHeight: { xs: 66, sm: 72, md: 78 }, bgcolor: "var(--bv-appbar-bg)" }} />

      <Drawer
        anchor="right"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        sx={{
          display: { xs: "block", lg: "none" },
          "& .MuiDrawer-paper": {
            width: "100%",
            maxWidth: "100vw",
            height: "100%",
            boxSizing: "border-box",
            bgcolor: "var(--bv-drawer-bg)",
          },
        }}
      >
        <Box sx={{ width: "100%", height: "100%", p: { xs: 2.5, sm: 3.5 }, display: "flex", flexDirection: "column", boxSizing: "border-box", overflowY: "auto" }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
            <Box component="img" src={theme === "dark" ? logoDark : logo} alt={t("common:brand")} sx={{ width: { xs: 160, sm: 190 }, height: "auto" }} />
            <Stack direction="row" spacing={1} alignItems="center">
              <ThemeToggle isMobile />
              <IconButton onClick={() => setMobileMenuOpen(false)} aria-label={t("common:buttons.closeMenu")} sx={{ border: "1px solid var(--bv-border)", borderRadius: "12px", p: 0.8 }}>
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
            </Stack>
          </Stack>
          <Stack spacing={0.8} sx={{ mb: 3 }}>
            {navLinks.map((item) => (
              <Button
                key={item.label}
                component="a"
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  color: colors.ink,
                  fontWeight: 700,
                  fontSize: "1.05rem",
                  py: 1.1,
                  px: 1.5,
                  borderRadius: "12px",
                  "&:hover": {
                    bgcolor: "var(--bv-surface-2)",
                    color: colors.tealDark,
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </Stack>
          <Divider sx={{ mb: 3 }} />
          <Stack spacing={1.5}>
            <Button variant="outlined" onClick={handleMobileDownloadMenuOpen} sx={{ ...secondaryBtnSx, py: 1.5, fontSize: "0.95rem" }}>{t("common:buttons.getApp")}</Button>
            <Button variant="contained" component="a" href={`${urlBase}signup`} target="_blank" rel="noopener noreferrer" sx={{ ...primaryBtnSx, py: 1.5, fontSize: "0.95rem" }}>{t("common:buttons.trial30")}</Button>
            <Button variant="contained" component="a" href={urlBase} target="_blank" rel="noopener noreferrer" sx={{ ...primaryBtnSx, bgcolor: colors.blue, "&:hover": { bgcolor: "#0ea5e9" }, py: 1.5, fontSize: "0.95rem" }}>{t("common:buttons.login")}</Button>
          </Stack>

          <Box sx={{ mt: "auto", pt: 4, pb: 1, textAlign: "center" }}>
            <Typography variant="caption" sx={{ color: colors.ink40, fontWeight: 600 }}>
              {t("common:footer.allRightsReserved", { year: new Date().getFullYear() })}
            </Typography>
          </Box>

          <Menu
            anchorEl={mobileDownloadMenuAnchor}
            open={Boolean(mobileDownloadMenuAnchor)}
            onClose={handleMobileDownloadMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            transformOrigin={{ vertical: "top", horizontal: "center" }}
            slotProps={{ paper: { sx: downloadMenuPaperSx } }}
          >
            <MenuItem
              component="a"
              href={appStore}
              target="_blank"
              rel="noopener noreferrer"
              sx={downloadMenuItemSx}
              onClick={() => {
                handleMobileDownloadMenuClose();
                setMobileMenuOpen(false);
              }}
            >
              <AppleIcon fontSize="small" sx={{ color: colors.blue }} />
              {t("common:buttons.iosDownload")}
            </MenuItem>
            <MenuItem
              component="a"
              href={googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              sx={downloadMenuItemSx}
              onClick={() => {
                handleMobileDownloadMenuClose();
                setMobileMenuOpen(false);
              }}
            >
              <AndroidIcon fontSize="small" sx={{ color: colors.tealDark }} />
              {t("common:buttons.androidDownload")}
            </MenuItem>
          </Menu>
        </Box>
      </Drawer>

      <Box sx={{ bgcolor: "#0f172a", py: 1.4, borderBottom: "1px solid rgba(255, 255, 255, 0.08)" }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, md: "52px" } }}>
          <Typography
            align="center"
            sx={{ color: "rgba(227,236,250,0.86)", fontWeight: 800, fontSize: { xs: 13, sm: 15 } }}
          >
            <Box component="span" sx={{ color: colors.teal, fontWeight: 800 }}>
              {t("common:topBanner.peopleNotProfit")}{" "}
            </Box>
            <Box component="span" sx={{ color: colors.blue, fontWeight: 800 }}>
              {convertPriceString(t("common:topBanner.plansStartingAt"))}
            </Box>
          </Typography>
        </Container>
      </Box>

      <Box
        sx={{
          background: "var(--bv-hero-gradient)",
          overflow: "visible",
        }}
      >
        <Container maxWidth="xl" sx={{ px: { xs: 0, sm: 2, md: 3, lg: 4 } }}>
          <section
            id="what-you-can-store"
            className="bv-hero"
            data-reveal
            data-reveal-immediate
            style={{ background: "transparent" }}
          >
            <div className="bv-hero__left" data-reveal-item>
              <h1 className="bv-hero__headline">
                <span className="bv-hero__line-teal">{t("home:hero.headline1")}</span>
                <span className="bv-hero__line-blue">{t("home:hero.headline2")}</span>
              </h1>

              <p className="bv-hero__subhead">
                {t("home:hero.subhead")}
              </p>

              <ul className="bv-hero__benefits">
                {ver2Benefits.map((benefit) => (
                  <li key={benefit.strong} className="bv-hero__benefit">
                    <span className="bv-hero__benefit-icon">{benefit.icon}</span>
                    <span className="bv-hero__benefit-text">
                      <strong>{benefit.strong}</strong> {benefit.text}
                    </span>
                  </li>
                ))}
              </ul>

              <div className="bv-hero__ctas">
                <a className="bv-hero__cta-primary" href={`${urlBase}signup`} target="_blank" rel="noopener noreferrer">
                  {t("common:buttons.startVault")}
                </a>
                <a className="bv-hero__cta-secondary" href="#how-it-works">
                  {t("common:buttons.seeHowItWorks")}
                </a>
              </div>

              <div className="bv-hero__categories">
                {ver2Categories.map((category) => (
                  <div key={category.title} className="bv-hero__cat-item">
                    <div className="bv-hero__cat-icon">{category.icon}</div>
                    <div className="bv-hero__cat-body">
                      <div className="bv-hero__cat-title">{category.title}</div>
                      <div className="bv-hero__cat-scenario">{category.scenario}</div>
                      <div className="bv-hero__cat-outcome">{category.outcome}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bv-hero__right" data-reveal-item>
              <div className="bv-hero__phone-wrap">
                <div className="bv-hero__glow" />

                <div className="bv-hero__badge bv-hero__badge--top">
                  <span className="bv-hero__badge-icon">⚡</span>
                  <div>
                    <div className="bv-hero__badge-name">{t("home:hero.badgeTop.name")}</div>
                    <div className="bv-hero__badge-note">{t("home:hero.badgeTop.note")}</div>
                  </div>
                </div>

                <img className="bv-hero__phone-img" src={theme === "dark" ? appScreenshotDark : appScreenshotLight} alt="BeforeVault app showing document vault" />

                <div className="bv-hero__badge bv-hero__badge--bottom">
                  <span className="bv-hero__badge-icon">✅</span>
                  <div>
                    <div className="bv-hero__badge-name">{t("home:hero.badgeBottom.name")}</div>
                    <div className="bv-hero__badge-note">{t("home:hero.badgeBottom.note")}</div>
                  </div>
                </div>
              </div>

              <p className="bv-hero__secure-line">{t("home:hero.secureLine")}</p>
            </div>
          </section>
        </Container>
      </Box>

      <Box data-reveal sx={{ py: { xs: 4, md: 5 }, borderTop: `1px solid ${colors.border}`, borderBottom: `1px solid ${colors.border}`, bgcolor: "var(--bv-surface)" }}>
        <Container maxWidth="lg">
          <Typography align="center" sx={{ letterSpacing: "0.12em", fontSize: 12, color: "#9ca3af", mb: 2.5, fontWeight: 700 }}>
            {t("common:footer.trustedBy")}
          </Typography>
          <Grid container spacing={2.5}>
            {testimonials.map((tItem, idx) => (
              <Grid item xs={12} md={4} key={tItem.name} data-reveal-item>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    bgcolor: "var(--bv-surface-2)",
                    border: idx === 2 ? `2px solid ${colors.teal}` : `1px solid var(--bv-border)`,
                    ...screenshotBlockHoverSx,
                  }}
                >
                  <CardContent sx={{ p: 3 }}>
                    <Stack direction="row" spacing={0.35} sx={{ color: "#f59e0b", mb: 1.8 }}>
                      {[1, 2, 3, 4, 5].map((n) => <StarIcon key={n} fontSize="small" />)}
                    </Stack>
                    <Typography sx={{ fontStyle: "italic", mb: 2.8, color: colors.ink, lineHeight: 1.65 }}>
                      {tItem.text}
                    </Typography>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Box
                        sx={{
                          width: 34,
                          height: 34,
                          borderRadius: "50%",
                          bgcolor: colors.teal,
                          color: "#fff",
                          fontWeight: 700,
                          fontSize: 14,
                          display: "grid",
                          placeItems: "center",
                          textTransform: "uppercase",
                        }}
                      >
                        {tItem.name.charAt(0)}
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: 700, color: colors.ink, lineHeight: 1.2 }}>{tItem.name}</Typography>
                        <Typography variant="caption" sx={{ color: colors.ink40 }}>{tItem.role}</Typography>
                      </Box>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container data-reveal maxWidth="lg" sx={{ py: { xs: 5, sm: 7, md: 9 } }}>
        <Grid container spacing={{ xs: 3, sm: 4, md: 6 }} alignItems="center">
          <Grid item xs={12} md={6} data-reveal-item>
            <Typography sx={{ fontSize: 12, color: colors.tealDark, letterSpacing: "0.1em", fontWeight: 700, mb: 1.2 }}>
              {t("home:reality.tag")}
            </Typography>
            <Typography variant="h3" sx={{ fontWeight: 800, color: colors.ink, mb: { xs: 2, md: 2.5 }, lineHeight: 1.12, fontSize: { xs: "clamp(26px, 7.5vw, 36px)", sm: "clamp(34px, 5.5vw, 44px)", md: "clamp(42px, 4vw, 54px)" }, letterSpacing: "-0.02em" }}>
              {t("home:reality.title")}{" "}
              <Box component="span" sx={{ color: colors.teal, textDecoration: "underline", textDecorationColor: "#EF4444", textDecorationThickness: { xs: "3px", md: "4px" }, textUnderlineOffset: "5px" }}>
                {t("home:reality.complicated")}
              </Box>
            </Typography>
            <Typography sx={{ color: colors.ink40, mb: 1.5, fontSize: { xs: 15, sm: 17, md: 19 }, lineHeight: 1.55 }}>
              {t("home:reality.desc1")}
            </Typography>
            <Typography sx={{ color: colors.ink60, fontWeight: 600, fontSize: { xs: 15, sm: 17, md: 19 }, lineHeight: 1.55 }}>
              {t("home:reality.desc2")}
            </Typography>
          </Grid>
          <Grid item xs={12} md={6} data-reveal-item>
            <Paper sx={{ p: { xs: 1.5, sm: 2.2, md: 2.5 }, borderRadius: 3, border: `1px solid ${colors.border}`, bgcolor: "var(--bv-card-bg)" }}>
              <Grid container spacing={1.5}>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: { xs: 1.5, sm: 1.8 }, bgcolor: theme === "dark" ? "rgba(239, 68, 68, 0.12)" : "#fff6f6", border: theme === "dark" ? "1px solid rgba(239, 68, 68, 0.3)" : "1px solid #f6d9d9", borderRadius: 2, height: "100%", ...cardHoverSx }}>
                    <Typography sx={{ fontWeight: 700, color: theme === "dark" ? "#f87171" : "#b91c1c", mb: 1.2, fontSize: 11, letterSpacing: "0.08em" }}>{t("home:reality.withoutTitle")}</Typography>
                    {withoutList.map((x) => (
                      <Stack key={x} direction="row" spacing={0.8} sx={{ mb: 0.8 }} alignItems="flex-start">
                        <CancelIcon fontSize="small" sx={{ color: theme === "dark" ? "#ef4444" : "#dc2626", mt: "2px", fontSize: 14, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: colors.ink, fontSize: { xs: 13, sm: 13.5, md: 14 }, lineHeight: 1.4 }}>{x}</Typography>
                      </Stack>
                    ))}
                  </Paper>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Paper sx={{ p: { xs: 1.5, sm: 1.8 }, bgcolor: theme === "dark" ? "rgba(0, 220, 188, 0.12)" : "#effcf9", border: theme === "dark" ? "1px solid rgba(0, 220, 188, 0.3)" : "1px solid #c7eee6", borderRadius: 2, height: "100%", ...cardHoverSx }}>
                    <Typography sx={{ fontWeight: 700, color: theme === "dark" ? colors.teal : colors.tealDark, mb: 1.2, fontSize: 11, letterSpacing: "0.08em" }}>{t("home:reality.withTitle")}</Typography>
                    {withList.map((x) => (
                      <Stack key={x} direction="row" spacing={0.8} sx={{ mb: 0.8 }} alignItems="flex-start">
                        <CheckCircleIcon fontSize="small" sx={{ color: theme === "dark" ? colors.teal : colors.tealDark, mt: "2px", fontSize: 14, flexShrink: 0 }} />
                        <Typography variant="body2" sx={{ color: colors.ink, fontSize: { xs: 13, sm: 13.5, md: 14 }, lineHeight: 1.4 }}>{x}</Typography>
                      </Stack>
                    ))}
                  </Paper>
                </Grid>
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      <Box data-reveal id="how-it-works" sx={{ bgcolor: colors.bg, py: { xs: 5, sm: 7, md: 9 }, scrollMarginTop: "96px" }}>
        <Container maxWidth="lg">
          <Typography align="center" sx={{ fontSize: 12, letterSpacing: "0.1em", color: colors.tealDark, fontWeight: 700, mb: 1 }}>{t("home:howItWorks.tag")}</Typography>
          <Typography align="center" variant="h3" sx={{ fontWeight: 800, color: colors.ink, mb: 1.5, fontSize: { xs: "clamp(26px, 7.5vw, 36px)", sm: "clamp(34px, 5.5vw, 44px)", md: "clamp(38px, 4vw, 48px)" }, lineHeight: 1.15, letterSpacing: "-0.02em" }}>
            {t("home:howItWorks.title1")}
            <br />
            <Box component="span" sx={{ color: colors.teal }}>{t("home:howItWorks.title2")}</Box>
          </Typography>
          <Typography align="center" sx={{ color: colors.ink40, mb: { xs: 3.5, sm: 4.5, md: 5 }, fontSize: { xs: 15, sm: 16.5, md: 18 }, maxWidth: 640, mx: "auto", lineHeight: 1.55 }}>{t("home:howItWorks.sub")}</Typography>
          <Grid container spacing={{ xs: 3.5, sm: 3 }}>
            {[
              { icon: <UploadFileIcon sx={{ fontSize: { xs: 24, md: 28 } }} />, title: t("home:howItWorks.step1.title"), text: t("home:howItWorks.step1.text") },
              { icon: <AccessTimeIcon sx={{ fontSize: { xs: 24, md: 28 } }} />, title: t("home:howItWorks.step2.title"), text: t("home:howItWorks.step2.text") },
              { icon: <BoltIcon sx={{ fontSize: { xs: 24, md: 28 } }} />, title: t("home:howItWorks.step3.title"), text: t("home:howItWorks.step3.text") },
            ].map((s, idx) => (
              <Grid item xs={12} sm={6} md={4} key={s.title} data-reveal-item sx={{ pt: { xs: "16px !important", sm: "0 !important" } }}>
                <Paper sx={{ p: { xs: 2.2, sm: 2.6, md: 3 }, borderRadius: 3, height: "100%", width: "100%", position: "relative", border: `1px solid ${colors.border}`, boxSizing: "border-box", ...cardHoverSx, bgcolor: "var(--bv-card-bg)" }}>
                  <Chip label={idx + 1} sx={{ position: "absolute", top: -12, left: 20, bgcolor: colors.teal, color: "#fff", fontWeight: 700, fontSize: 13, height: 24 }} />
                  <Box sx={{ width: { xs: 48, md: 56 }, height: { xs: 48, md: 56 }, borderRadius: 2, bgcolor: theme === "dark" ? "rgba(0, 220, 188, 0.14)" : "#eef1f4", color: colors.tealDark, display: "grid", placeItems: "center", mt: 1.5, mb: 2 }}>{s.icon}</Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: colors.ink, mb: 1, fontSize: { xs: "1.08rem", sm: "1.18rem", md: "1.25rem" } }}>{s.title}</Typography>
                  <Typography sx={{ color: colors.ink40, fontSize: { xs: 13.5, sm: 14, md: 14.5 }, lineHeight: 1.55 }}>{s.text}</Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      <Container data-reveal maxWidth="lg" sx={{ py: { xs: 8, md: 10 } }}>
        <Typography align="center" sx={{ fontSize: 12, letterSpacing: "0.1em", color: colors.tealDark, fontWeight: 700, mb: 2 }}>{t("home:whoItsFor.tag")}</Typography>
        <Typography align="center" variant="h3" sx={{ fontWeight: 800, color: colors.ink, mb: 5 }}>
          {t("home:whoItsFor.title1")}
          <br />
          <Box component="span" sx={{ color: colors.teal }}>{t("home:whoItsFor.title2")}</Box>
        </Typography>
        <Box className="who-cards-flex" sx={whoFlexSx}>
          {whoItsFor.map((w) => {
            const Icon = w.Icon;
            return (
              <Paper key={w.title} className="who-card" elevation={0} sx={whoCardSx} data-reveal-item>
                <Box>
                  <Box
                    className="who-icon-wrap"
                    sx={{
                      width: { xs: 44, md: 48 },
                      height: { xs: 44, md: 48 },
                      borderRadius: 2,
                      bgcolor: colors.bg,
                      color: colors.tealDark,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mb: { xs: 1, md: 1.25 },
                      flexShrink: 0,
                      transition: "background 0.2s ease",
                    }}
                  >
                    <Icon sx={{ fontSize: { xs: 22, md: 24 } }} />
                  </Box>
                  <Typography
                    sx={{
                      fontFamily: headingFont,
                      fontWeight: 800,
                      color: colors.ink,
                      fontSize: { xs: "0.9375rem", md: "1rem" },
                      lineHeight: 1.25,
                      mb: 0.75,
                    }}
                  >
                    {w.title}
                  </Typography>
                  <Typography
                    sx={{
                      color: colors.ink40,
                      fontSize: { xs: "0.8125rem", md: "0.875rem" },
                      lineHeight: 1.5,
                      fontFamily: bodyFont,
                    }}
                  >
                    {w.text}
                  </Typography>
                </Box>
                <Box
                  component="a"
                  href={`${urlBase}signup`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="who-link"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5,
                    fontSize: { xs: "0.8125rem", md: "0.875rem" },
                    fontWeight: 700,
                    color: colors.tealDark,
                    textDecoration: "none",
                    flexShrink: 0,
                    mt: "auto",
                    pt: { xs: 0.75, md: 1 },
                    transition: "gap 0.2s ease, color 0.2s ease",
                    "&:hover": { color: colors.teal },
                  }}
                >
                  {t("common:buttons.startFree")}
                  <ArrowForwardIcon sx={{ fontSize: 15 }} />
                </Box>
              </Paper>
            );
          })}
        </Box>
      </Container>

      <Security2026 />

      <Pricing2026 />

      <Box id="faqs" sx={{ bgcolor: colors.bg, py: 8, scrollMarginTop: "96px" }}>
        <Container maxWidth="lg">
          <Box data-reveal>
            <Typography align="center" sx={{ fontSize: 12, letterSpacing: "0.1em", color: colors.tealDark, fontWeight: 700, mb: 1 }}>
              {t("home:faq.tag")}
            </Typography>
            <Typography align="center" variant="h3" sx={{ fontWeight: 800, color: colors.ink, mb: 2 }}>
              {t("home:faq.title1")}
              <br />
              <Box component="span" sx={{ color: colors.teal }}>{t("home:faq.title2")}</Box>
            </Typography>
            <Box sx={{ mt: 3 }}>
              {faqItems.map((item, idx) => (
                <Accordion
                  key={item.q}
                  expanded={openFaq === idx}
                  onChange={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                  disableGutters
                  sx={{
                    mb: 2,
                    borderRadius: "10px !important",
                    border: `1px solid ${colors.border}`,
                    boxShadow: "none",
                    "&:before": { display: "none" },
                    ...cardHoverSx,
                  }}
                >
                  <AccordionSummary
                    expandIcon={
                      <Box
                        sx={{
                          width: 22,
                          height: 22,
                          borderRadius: "50%",
                          border: `1px solid ${colors.border}`,
                          color: colors.tealDark,
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <AddIcon sx={{ fontSize: 14 }} />
                      </Box>
                    }
                    sx={{
                      minHeight: 62,
                      px: 2,
                      transition: "background-color 180ms ease",
                      "&:hover": { bgcolor: "var(--bv-surface-2)" },
                    }}
                  >
                    <Typography sx={{ fontWeight: 700, color: colors.ink }}>{item.q}</Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, px: 2, pb: 2 }}>
                    <Typography sx={{ color: colors.ink40, lineHeight: 1.7 }}>{item.a}</Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          </Box>
          <Divider sx={{ my: 4 }} />
          <Paper
            data-reveal
            sx={{
              mt: 1,
              p: { xs: "32px 18px", sm: "40px 28px", md: 6 },
              borderRadius: { xs: 3, md: 4 },
              color: "#fff",
              textAlign: "center",
              border: "1px solid rgba(20, 35, 56, 0.9)",
              background:
                "radial-gradient(circle at 12% 82%, rgba(24,180,236,0.18) 0%, rgba(24,180,236,0) 35%), radial-gradient(circle at 88% 18%, rgba(0,220,188,0.18) 0%, rgba(0,220,188,0) 35%), linear-gradient(160deg, #030c22 0%, #07112d 55%, #050b1f 100%)",
            }}
          >
            <Typography sx={{ letterSpacing: "0.1em", fontSize: 11, fontWeight: 700, color: colors.teal, mb: 1.5 }}>
              {t("home:bottomCta.tag")}
            </Typography>
            <Typography
              align="center"
              variant="h3"
              sx={{
                fontWeight: 800,
                color: "#fff !important",
                mb: 1.8,
                lineHeight: 1.15,
                fontSize: { xs: "clamp(24px, 6.8vw, 32px)", sm: "clamp(30px, 5vw, 40px)", md: "clamp(38px, 4vw, 48px)" },
                letterSpacing: "-0.02em",
                maxWidth: 720,
                mx: "auto",
              }}
            >
              {t("home:bottomCta.title1")}{" "}
              <Box component="span" sx={{ color: colors.teal, display: { xs: "inline", sm: "inline" } }}>
                {t("home:bottomCta.title2")}
              </Box>
            </Typography>
            <Typography align="center" sx={{ color: "rgba(227,236,250,0.8)", mb: { xs: 3, md: 4 }, fontSize: { xs: 14.5, sm: 16, md: 18 }, lineHeight: 1.55, maxWidth: 660, mx: "auto" }}>
              {t("home:bottomCta.desc1")} {t("home:bottomCta.desc2")}
            </Typography>
            <Stack direction="row" justifyContent="center">
              <Button
                variant="contained"
                component="a"
                href={`${urlBase}signup`}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  ...primaryBtnSx,
                  px: { xs: 2.5, sm: 4, md: 5.5 },
                  py: { xs: 1.35, md: 1.6 },
                  fontSize: { xs: "0.84rem", sm: "0.95rem", md: "1.05rem" },
                  fontWeight: 700,
                  lineHeight: 1.35,
                  textAlign: "center",
                  whiteSpace: "normal",
                  wordBreak: "normal",
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: "auto",
                  maxWidth: "100%",
                  boxShadow: "0 10px 24px rgba(0,220,188,0.35)",
                }}
              >
                {t("common:buttons.startFree30TrialCta")}
              </Button>
            </Stack>
          </Paper>
        </Container>
      </Box>

      <Box id="contact-us" sx={{ bgcolor: "var(--bv-surface)", pt: { xs: 3.5, md: 4 }, borderTop: `1px solid ${colors.border}`, scrollMarginTop: "96px" }}>
        <Container maxWidth="lg" sx={{ pb: { xs: 3, md: 3.5 } }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            spacing={{ xs: 2.5, md: 2.2 }}
            alignItems="center"
            sx={{ textAlign: { xs: "center", md: "left" } }}
          >
            <Box component="img" src={theme === "dark" ? logoDark : logo} alt={t("common:brand")} sx={{ width: { xs: 150, sm: 170 }, height: "auto" }} />
            <Stack
              direction="row"
              spacing={{ xs: 1.5, sm: 2.2, md: 2.5 }}
              alignItems="center"
              justifyContent="center"
              sx={{
                flexWrap: "wrap",
                gap: { xs: 1.2, sm: 1.6 },
                rowGap: { xs: 1.2, sm: 1.6 },
              }}
            >
              {[
                { label: t("common:nav.home"), href: "/#what-you-can-store" },
                { label: t("common:nav.howItWorks"), href: "/#how-it-works" },
                { label: t("common:nav.pricing"), href: "/#pricing" },
                { label: t("common:nav.security"), href: "/#security" },
                { label: t("common:nav.faqs"), href: "/#faqs" },
              ].map((link) => (
                <Typography
                  key={link.label}
                  component="a"
                  href={link.href}
                  variant="body2"
                  sx={{
                    textDecoration: "none",
                    color: colors.ink60,
                    fontWeight: 600,
                    fontSize: { xs: "0.88rem", sm: "0.92rem", md: "0.95rem" },
                    transition: "color 180ms ease",
                    "&:hover": { color: colors.tealDark },
                  }}
                >
                  {link.label}
                </Typography>
              ))}
              <LanguageSelector />
            </Stack>
          </Stack>
        </Container>
        <Box sx={{ bgcolor: "var(--bv-footer-bottom-bg)", py: 1.5 }}>
          <Stack spacing={0.4} alignItems="center">
            <Typography align="center" sx={{ color: "#fff", fontSize: 13 }}>
              {t("common:footer.allRightsReserved", { year: new Date().getFullYear() })}
            </Typography>
            <Stack direction="row" spacing={1.1} alignItems="center">
              <Typography
                component="button"
                onClick={() => openLegalModal("privacy")}
                sx={{
                  background: "none",
                  border: "none",
                  p: 0,
                  m: 0,
                  cursor: "pointer",
                  color: "#e6f8ff",
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                {t("common:footer.privacyPolicy")}
              </Typography>
              <Typography sx={{ color: "#d9f4ff", fontSize: 12 }}>|</Typography>
              <Typography
                component="button"
                onClick={() => openLegalModal("terms")}
                sx={{
                  background: "none",
                  border: "none",
                  p: 0,
                  m: 0,
                  cursor: "pointer",
                  color: "#e6f8ff",
                  fontSize: 12.5,
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                {t("common:footer.termsAndConditions")}
              </Typography>
            </Stack>
          </Stack>
        </Box>
        <Box sx={{ bgcolor: "var(--bv-dark-section-bg)", py: 1.4, px: 2 }}>
          <Typography
            align="center"
            sx={{ color: "rgba(227,236,250,0.86)", fontWeight: 800, fontSize: { xs: 13, sm: 15 } }}
          >
            <Box component="span" sx={{ color: "rgba(227,236,250,0.86)", fontWeight: 800 }}>
              {t("common:footer.madeForEveryone")}{" "}
            </Box>
            <Box component="span" sx={{ color: colors.teal, fontWeight: 800 }}>
              {t("common:footer.privacyNotPremium")}
            </Box>
          </Typography>
        </Box>
      </Box>

      <Dialog
        open={Boolean(legalModalType)}
        onClose={closeLegalModal}
        fullWidth
        maxWidth="md"
        scroll="paper"
        PaperProps={{
          sx: {
            borderRadius: { xs: 2, md: 3 },
            overflow: "hidden",
            maxHeight: { xs: "92vh", md: "88vh" },
            width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)", md: "800px" },
            m: { xs: "8px", sm: "16px", md: "auto" },
            bgcolor: "var(--bv-surface)",
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 800,
            color: "#fff",
            bgcolor: "#0f172a",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            py: { xs: 1.3, sm: 1.6 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: { xs: 16, sm: 18, md: 19 } }}>
            {legalModalType === "privacy" ? t("common:footer.privacyPolicy") : t("common:footer.termsAndConditions")}
          </Typography>
          <IconButton
            onClick={closeLegalModal}
            aria-label={t("common:buttons.closeLegalModal")}
            sx={{ color: "#cbd5e1", p: 0.8 }}
          >
            <CloseIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            p: { xs: 1.5, sm: 2.5, md: 3 },
            bgcolor: "var(--bv-surface)",
            borderColor: "var(--bv-border)",
            "& .privacy": {
              p: "0 !important",
              pt: "0 !important",
            },
            "& .privacy .MuiTypography-h3": {
              display: "none",
            },
            "& .privacy .MuiGrid-container": {
              m: "0 !important",
              width: "100% !important",
            },
            "& .privacy h2, & .privacy h3": {
              scrollMarginTop: "80px",
            },
            "& h2": {
              color: "var(--bv-teal) !important",
              fontWeight: 700,
            },
            "& h3, & h4, & h5, & h6": {
              color: "var(--bv-ink) !important",
              fontWeight: 700,
            },
            "& strong, & b, & [style*='Heavy'], & [style*='bold'], & [style*='Bold'], & span[style*='Heavy'], & span[style*='bold'], & span[style*='Bold']": {
              color: "var(--bv-ink) !important",
              fontWeight: 700,
            },
            "& p, & li, & span, & div": {
              color: "var(--bv-ink-60) !important",
              lineHeight: 1.75,
            },
            "& a, & a *": {
              color: "var(--bv-teal) !important",
              fontWeight: 600,
            },
          }}
        >
          <Box
            sx={{
              bgcolor: "var(--bv-surface)",
              "& *": {
                color: "var(--bv-ink-60) !important",
              },
              "& h2": {
                color: "var(--bv-teal) !important",
                fontWeight: 700,
              },
              "& h3, & h4, & h5, & h6": {
                color: "var(--bv-ink) !important",
                fontWeight: 700,
              },
              "& strong, & b, & [style*='Heavy'], & [style*='bold'], & [style*='Bold'], & span[style*='Heavy'], & span[style*='bold'], & span[style*='Bold']": {
                color: "var(--bv-ink) !important",
                fontWeight: 700,
              },
              "& p, & li, & span, & div": {
                color: "var(--bv-ink-60) !important",
                lineHeight: 1.75,
              },
              "& a, & a *": {
                color: "var(--bv-teal) !important",
                fontWeight: 600,
              },
            }}
          >
            {legalModalType === "privacy" ? <PrivacyPolicy /> : <Terms />}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}
